<template>
  <div class="storage-info p-4">
    <h3 class="text-lg font-semibold mb-3">Storage usage</h3>
    
    <div v-if="storageLoading" class="text-center p-8">
      <ProgressSpinner />
      <p class="mt-2 text-gray-600">Loading storage information...</p>
    </div>
    
    <div v-else-if="storageError" class="text-center p-8 text-red-500">
      <p class="mb-4">{{ storageError }}</p>
      <Button label="Retry" @click="loadStorageData" severity="secondary" :disabled="!props.tenant" />
    </div>
    
    <div v-else-if="storageData" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="bg-white p-4 rounded-lg shadow-sm flex justify-center items-center">
        <StorageChart  :data="storageChartData"  :options="storageChartOptions" />
      </div>
      <div class="space-y-4">
        <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div class="text-sm font-semibold text-blue-700">Legal Cases</div>
          <div class="text-2xl font-bold text-blue-900">{{ formatBytes(storageData.legal_case_bytes) }}</div>
          <div class="text-sm text-blue-600">{{ calculatePercentage(storageData.legal_case_bytes) }}% of total</div>
        </div>
        
        <div class="bg-green-50 p-4 rounded-lg border border-green-200">
          <div class="text-sm font-semibold text-green-700">Resources</div>
          <div class="text-2xl font-bold text-green-900">{{ formatBytes(storageData.resource_bytes) }}</div>
          <div class="text-sm text-green-600">{{ calculatePercentage(storageData.resource_bytes) }}% of total</div>
        </div>
        
        <div class="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <div class="text-sm font-semibold text-amber-700">Radiology</div>
          <div class="text-2xl font-bold text-amber-900">{{ formatBytes(storageData.radiology_bytes || 0) }}</div>
          <div class="text-sm text-amber-600">{{ calculatePercentage(storageData.radiology_bytes || 0) }}% of total</div>
        </div>
        
        <div class="bg-gray-50 p-4 rounded-lg border-2 border-gray-300">
          <div class="text-sm font-semibold text-gray-700">Total Storage</div>
          <div class="text-2xl font-bold text-gray-900">{{ formatBytes(storageData.total_bytes) }}</div>
          <div class="text-sm text-gray-600">Used across all resources</div>
        </div>
      </div>
    </div>
    
    <div v-else class="text-center p-8 text-gray-500">
      <p class="mb-4">No storage data available for tenant: {{ props.tenant || 'N/A' }}</p>
      <Button label="Load Storage Data" @click="loadStorageData" severity="secondary" :disabled="!props.tenant" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import storageService from '../utils/storageService';
import StorageChart from './StorageChart.vue';

const props = defineProps({
  tenant: {
    type: String,
    default: ''
  }
});

const storageData = ref(null);
const storageLoading = ref(false);
const storageError = ref(null);

const loadStorageData = async () => {
  if (!props.tenant) {
    storageError.value = 'No tenant information provided to load storage data.';
    storageData.value = null;
    storageLoading.value = false;
    return;
  }
  
  storageLoading.value = true;
  storageError.value = null;
  
  try {
    const response = await storageService.getStorageData(props.tenant);
    if (response && response.data && response.data.storage_details) {
      storageData.value = response.data.storage_details;
    } else {
      storageData.value = null;
      storageError.value = 'Storage data not found in response.';
      console.warn('Unexpected storage data response structure:', response);
    }
  } catch (error) {
    console.error('Error loading storage data in InstanceStorageInfo:', error);
    storageError.value = error.message || 'Failed to load storage information.';
    storageData.value = null;
  } finally {
    storageLoading.value = false;
  }
};

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined || bytes < 0) return 'N/A';
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const calculatePercentage = (value) => {
  if (!storageData.value?.total_bytes || storageData.value.total_bytes === 0 || value === null || value === undefined) return 0;
  return Math.round((value / storageData.value.total_bytes) * 100);
};

const storageChartData = computed(() => {
  if (!storageData.value) return null;
  
  return {
    labels: ['Legal Cases', 'Resources', 'Radiology'],
    datasets: [{
      data: [
        storageData.value.legal_case_bytes || 0, 
        storageData.value.resource_bytes || 0,
        storageData.value.radiology_bytes || 0
      ],
      backgroundColor: ['#3B82F6', '#10B981', '#F59E0B'],
      borderColor: ['#1D4ED8', '#059669', '#D97706'],
      borderWidth: 2
    }]
  };
});

const storageChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        padding: 20,
        usePointStyle: true
      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const label = context.label || '';
          const value = formatBytes(context.raw);
          const percentage = calculatePercentage(context.raw);
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    }
  }
}));

watch(() => props.tenant, (newTenant) => {
  if (newTenant) {
    loadStorageData();
  } else {
    storageData.value = null;
    storageError.value = 'Tenant not provided.';
    storageLoading.value = false;
  }
}, { immediate: true });

onMounted(() => {
  if (!props.tenant) {
    storageLoading.value = false;
    storageError.value = 'Tenant information is not yet available for storage data.';
  }
});

</script>

<style scoped>
.storage-info {
  min-height: 300px;
}
</style> 